import React from 'react';
import { DatePicker, DatePickerProps } from '@mui/x-date-pickers/DatePicker';
import { PickersCalendarHeaderProps } from '@mui/x-date-pickers/PickersCalendarHeader';
import { PickersDay, PickersDayProps } from '@mui/x-date-pickers/PickersDay';
import { PickersActionBar, PickersActionBarProps } from '@mui/x-date-pickers/PickersActionBar';
import { usePickerContext } from '@mui/x-date-pickers/hooks';
import dayjs, { Dayjs } from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezonePlugin from 'dayjs/plugin/timezone';
import { useTheme } from '@mui/material';
import { IconButton } from '../IconButton';
import { Box } from '../Box';
import { Flex } from '../Flex';
import { Button } from '../Button';
import { Icon } from '../Icon';
import { Text } from '../Text';
import {
  PounceStyleProps,
  extractPounceProps,
  mapPouncePropsToSx,
  getEffectiveDateFormat,
} from '../../utils';
import { TimePicker } from './TimePicker';

dayjs.extend(utc);
dayjs.extend(timezonePlugin);

// Custom calendar header
const CustomCalendarHeader = (props: PickersCalendarHeaderProps) => {
  const { onMonthChange, currentMonth } = props;

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        paddingX: '4px',
        paddingY: '12px',
      }}
    >
      <Button
        variant="ghost"
        size="medium"
        aria-label="Go to previous month"
        onClick={() => onMonthChange(currentMonth.subtract(1, 'month'))}
      >
        <Icon type="chevron-left" />
      </Button>
      <Text
        variant="label/lg"
        fontWeight="semibold"
        sx={{
          flex: 1,
          textAlign: 'center',
        }}
      >
        {currentMonth.format('MMMM YYYY')}
      </Text>
      <Button
        variant="ghost"
        size="medium"
        aria-label="Go to next month"
        onClick={() => onMonthChange(currentMonth.add(1, 'month'))}
      >
        <Icon type="chevron-right" />
      </Button>
    </Box>
  );
};

// Custom day component with pounce-compatible aria-label
const CustomDay = (props: PickersDayProps) => {
  const { day, ...other } = props;
  // Format: "Su Nov 01 2020" to match original pounce behavior
  const ariaLabel = day.format('dd MMM DD YYYY');

  return <PickersDay {...other} day={day} aria-label={ariaLabel} />;
};

// Custom action bar that includes TimePicker when withTime is enabled
interface CustomActionBarProps extends PickersActionBarProps {
  withTime?: boolean;
  mode?: '12h' | '24h';
  disableSelection?: (date: Dayjs) => boolean;
  timezone?: 'local' | 'utc';
}

const CustomActionBar = (props: CustomActionBarProps) => {
  const { withTime, mode, disableSelection, timezone, ...actionBarProps } = props;
  const pickerContext = usePickerContext<Dayjs>();
  const theme = useTheme();

  const handleTimeUpdate = React.useCallback(
    (updatedDate: Dayjs | null) => {
      pickerContext?.setValue(updatedDate);
      // Workaround to prevent MUI to close the calendar view, force the picker to stay open after setValue
      requestAnimationFrame(() => {
        pickerContext?.setOpen?.(true);
      });
    },
    [pickerContext],
  );

  // Get current draft value from MUI's context
  let currentValue: Dayjs | null = null;

  if (pickerContext?.value) {
    currentValue = pickerContext.value;
  } else if (withTime) {
    currentValue = timezone === 'utc' ? dayjs.utc() : dayjs();
  }

  return (
    <>
      {withTime && disableSelection && (
        <Flex
          align="center"
          spacing={3}
          sx={{
            p: '16px',
            border: '1px solid',
            borderTop: 'none',
            borderColor: theme.palette.componentTokens?.calendar.colors.container.stroke.default,
            backgroundColor:
              theme.palette.componentTokens?.calendar.colors.container.background.default,
            justifyContent: 'center',
          }}
        >
          <TimePicker
            date={currentValue}
            mode={mode}
            onTimeUpdate={handleTimeUpdate}
            disableSelection={disableSelection}
            label="Time"
          />
        </Flex>
      )}
      <PickersActionBar {...actionBarProps} />
    </>
  );
};

export interface DateInputProps
  extends
    Omit<
      DatePickerProps,
      'value' | 'onChange' | 'format' | 'minDate' | 'maxDate' | 'variant' | keyof PounceStyleProps
    >,
    PounceStyleProps {
  /**
   * The format displayed in the input element
   */
  format?: string;

  /**
   * A date that works as a value
   */
  value?: Date;

  /**
   * The minimum date that a user is allowed to select
   */
  minDate?: Date;

  /**
   * The maximum date that a user is allowed to select
   */
  maxDate?: Date;

  /**
   * Specifies the timezone that will be used when selecting a date
   */
  timezone?: 'local' | 'utc';

  /**
   * A callback for whenever the value of the chosen date changes.
   */
  onChange: (date?: Date) => void;

  /**
   * The placeholder text for the input
   */
  placeholder?: string;

  /**
   * Whether the component should allow manipulating time
   * Note: For time support, use DateTimePicker instead
   */
  withTime?: boolean;

  /**
   * Whether the input is disabled
   */
  disabled?: boolean;

  /**
   * Label for the input
   */
  label?: string;

  /**
   * A flag that allows clearing the values
   * defaults to false
   */
  disableReset?: boolean;

  /**
   * Date input format for time picker
   */
  mode?: '12h' | '24h';

  /**
   * The position to expand the picker
   */
  alignment?: 'left' | 'right' | 'match-width';

  /**
   * A flag that dictates if the component should allow toggling the picker
   * defaults to true
   */
  withIcon?: boolean;

  /**
   * A render prop that allows you to customize the input element
   * defaults to a simple text input
   */
  renderInput?: (props) => React.ReactNode;

  /**
   * Additional styles to apply (combined with pounce props)
   */
  sx?: DatePickerProps['sx'];
}

/**
 * Convert Date to Dayjs with timezone support
 */
const dateToDayjs = (date: Date | undefined, timezone: 'local' | 'utc' = 'local'): Dayjs | null => {
  if (!date) {
    return null;
  }
  return timezone === 'utc' ? dayjs.utc(date) : dayjs(date);
};

/**
 * A component to help selecting dates in forms.
 * Built on top of MUI DatePicker with the same API as the original pounce DateInput.
 */
export const DateInput = React.forwardRef<HTMLDivElement, DateInputProps>(function DateInput(
  {
    value,
    format = 'MM/DD/YYYY',
    minDate,
    maxDate,
    onChange,
    timezone = 'local',
    disabled = false,
    label,
    placeholder,
    withTime,
    mode = '24h',
    disableReset = false,
    alignment = 'left',
    withIcon = true,
    renderInput,
    sx,
    ...rest
  },
  ref,
) {
  const { pounceProps, otherProps } = extractPounceProps(rest);
  const pounceStyles = mapPouncePropsToSx(pounceProps);

  // State to control picker open/close when using renderInput
  const [pickerOpen, setPickerOpen] = React.useState(false);

  // Compute effective format with time tokens when withTime is enabled
  const effectiveFormat = React.useMemo(
    () => getEffectiveDateFormat(format, withTime, mode),
    [format, withTime, mode],
  );

  // Convert Date values to Dayjs
  const dayjsValue = React.useMemo(() => dateToDayjs(value, timezone), [value, timezone]);
  const dayjsMinDate = React.useMemo(() => dateToDayjs(minDate, timezone), [minDate, timezone]);
  const dayjsMaxDate = React.useMemo(() => dateToDayjs(maxDate, timezone), [maxDate, timezone]);

  // Format the current value for display
  const formattedValue = React.useMemo(() => {
    return dayjsValue ? dayjsValue.format(effectiveFormat) : '';
  }, [dayjsValue, effectiveFormat]);

  // Handle change event from date picker (only called on Accept)
  const handleChange = React.useCallback(
    (newValue: Dayjs | null) => {
      if (newValue === null) {
        onChange(undefined);
      } else {
        // Convert back to Date with proper precision
        const precision = withTime ? 'minute' : 'day';
        // Ensure UTC timezone is preserved when converting to Date
        const dateValue =
          timezone === 'utc'
            ? dayjs.utc(newValue).startOf(precision).toDate()
            : newValue.startOf(precision).toDate();
        onChange(dateValue);
      }
    },
    [onChange, withTime, timezone],
  );

  // Check if date selection is disabled
  const shouldDenySelectedDate = React.useCallback(
    (selectedDate: Dayjs) => {
      return (
        (minDate && selectedDate?.isBefore(minDate)) || (maxDate && selectedDate?.isAfter(maxDate))
      );
    },
    [minDate, maxDate],
  );

  // Combine pounce styles with user sx
  const combinedSx = {
    ...pounceStyles,
    ...(sx || {}),
  };

  // Calculate popper placement and modifiers based on alignment
  const popperConfig = React.useMemo(() => {
    switch (alignment) {
      case 'right':
        return {
          placement: 'bottom-end' as const,
          matchWidth: false,
        };
      case 'match-width':
        return {
          placement: 'bottom-start' as const,
          matchWidth: true,
        };
      case 'left':
      default:
        return {
          placement: 'bottom-start' as const,
          matchWidth: false,
        };
    }
  }, [alignment]);

  const commonProps = {
    ref,
    value: dayjsValue,
    onChange: handleChange,
    format: effectiveFormat,
    minDate: dayjsMinDate,
    maxDate: dayjsMaxDate,
    disabled,
    label,
    closeOnSelect: false,
    desktopModeMediaQuery: '@media (pointer: fine), (pointer: coarse)',
    sx: combinedSx,
    dayOfWeekFormatter: (day: Dayjs) => day.format('dd'),
    ...(renderInput && {
      open: pickerOpen,
      onOpen: () => setPickerOpen(true),
      onClose: () => setPickerOpen(false),
    }),
    localeText: {
      okButtonLabel: 'Apply',
      cancelButtonLabel: 'Cancel',
      clearButtonLabel: withTime ? 'Clear Date & Time' : 'Clear Date',
    },
    slots: {
      openPickerButton: withIcon
        ? (props: any) => (
            <IconButton
              icon="calendar"
              {...props}
              size="small"
              variant="ghost"
              aria-label="Toggle picker"
            />
          )
        : () => null,
      calendarHeader: CustomCalendarHeader,
      day: CustomDay,
      actionBar: CustomActionBar,
    },
    // Spread otherProps without slotProps so the merged slotProps block below wins
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    ...(({ slotProps: _ignored, ...otherPropsWithoutSlotProps }) => otherPropsWithoutSlotProps)(
      otherProps as any,
    ),
    slotProps: {
      ...((otherProps as any).slotProps || {}),
      popper: {
        placement: popperConfig.placement,
        modifiers: [
          {
            name: 'offset',
            options: {
              offset: [0, 16],
            },
          },
          ...(popperConfig.matchWidth
            ? [
                {
                  name: 'matchWidth',
                  enabled: true,
                  phase: 'beforeWrite' as const,
                  requires: ['computeStyles'],
                  fn: ({ state }: any) => {
                    const width = `${state.rects.reference.width}px`;
                    return {
                      ...state,
                      styles: {
                        ...state.styles,
                        popper: {
                          ...state.styles.popper,
                          width: 'auto',
                          minWidth: width,
                        },
                      },
                    };
                  },
                  effect: ({ state }: any) => {
                    const referenceWidth = (state.elements.reference as HTMLElement).offsetWidth;
                    const popperElement = state.elements.popper as HTMLElement;
                    popperElement.style.minWidth = `${referenceWidth}px`;
                    popperElement.style.width = 'auto';
                  },
                },
              ]
            : []),
        ],
      },
      actionBar: {
        actions: disableReset ? ['cancel', 'accept'] : ['cancel', 'accept', 'clear'],
        withTime,
        mode,
        disableSelection: shouldDenySelectedDate,
        timezone,
      } as any,
    },
  };

  const pickerComponent = <DatePicker {...(commonProps as DatePickerProps)} />;

  // If renderInput is provided, wrap the picker with custom input
  if (renderInput) {
    return (
      <Box sx={{ position: 'relative' }}>
        {/* Hide the MUI input visually but keep it functional */}
        <Box
          sx={{
            position: 'absolute',
            opacity: 0,
            pointerEvents: 'none',
            width: '100%',
            zIndex: -1,
          }}
        >
          {pickerComponent}
        </Box>
        {/* Render custom input on top */}
        <Box sx={{ cursor: disabled ? 'not-allowed' : 'pointer' }}>
          {renderInput({
            openDatePicker: () => setPickerOpen(true),
            formattedValue,
            effectiveFormat,
            label,
            disabled,
            placeholder,
          })}
        </Box>
      </Box>
    );
  }

  return pickerComponent;
});
